/**
 * `DownbeatRule` — a full-width hairline with a 44px, 3px `--accent` bar flush left.
 * `docs/design-direction.md` §6.10.
 *
 * **This is the only heading decoration in the entire system.** There is no other
 * divider, no accent underline, no coloured card header, and no icon beside a
 * section title. The bar is `--drop` (44px) wide, which is simultaneously the
 * rhythmic emphasis of §5.1's bar-of-four, the width of every section rule and the
 * minimum touch target — the one genuine joke in the system, and it costs nothing.
 *
 * Composition (§8.1, §8.4, §8.6): `--drop` of air, the rule, then the section's mono
 * caps heading beneath it. The heading is optional because the rule is also used bare
 * to separate two blocks of the same screen (Now Playing's spec table, the set list's
 * `PLAYED` history).
 *
 * Copy rules the heading obeys (§4.3, §11): the mono caps tier is licensed for Latin
 * and numerals; a Russian heading keeps its tracking dropped by the `cyr` modifier so
 * it does not shout, because tracked uppercase Cyrillic is banned.
 */

import type { JSX } from 'react';

import { hasCyrillic } from '@bl/shared';

import styles from './primitives.module.css';
import { cx } from './Rule';

export interface DownbeatRuleProps {
  /**
   * Optional section heading printed under the rule, in the mono caps `--fs-13`
   * tier. Omitted entirely when absent — never rendered as an empty line.
   */
  readonly label?: string | undefined;
  /**
   * Heading level for the label, so a screen's outline stays correct. Default `2`.
   * Ignored when there is no label.
   */
  readonly level?: 2 | 3;
  readonly className?: string | undefined;
}

export function DownbeatRule({ label, level = 2, className }: DownbeatRuleProps): JSX.Element {
  const Heading = level === 2 ? 'h2' : 'h3';
  const text = typeof label === 'string' ? label.trim() : '';

  return (
    <div className={cx(styles.section, className)}>
      <hr className={cx('rule', 'rule--downbeat')} data-downbeat="true" />
      {text === '' ? null : (
        <Heading className={cx('t-mono', 't-13', styles.heading, hasCyrillic(text) && styles.cyr)}>
          {text}
        </Heading>
      )}
    </div>
  );
}
